import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { ArrowRightIcon, PlusIcon } from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { toast } from 'sonner';

import { type DraftStep, WorkflowEditorStepRow } from './WorkflowEditorStepRow';
import { WorkflowLibraryCardPreview } from './WorkflowLibraryCardPreview';
import { ALL_OUTPUT_KINDS, OUTPUT_KIND_LABEL } from './workflow-utils';

import { DiscoverPill } from '@/components/discover/DiscoverPill';
import { Button } from '@/components/ui/button';
import { FullScreenSplitDialog } from '@/components/ui/full-screen-split-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { queryKeys } from '@/config/queryClient';
import { ITEM_META } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import {
  type WorkflowOutputKind,
  type WorkflowStep,
  type WorkflowTemplate,
  campaignService,
} from '@/services/data/CampaignService';

interface NewWorkflowDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided, the editor opens in EDIT mode pre-filled from this template. */
  editTemplate?: WorkflowTemplate | null;
}

interface StarterTemplate {
  id: string;
  name: string;
  description: string;
  outputKinds: WorkflowOutputKind[];
  steps: Omit<DraftStep, 'id'>[];
}

const STARTER_TEMPLATES: StarterTemplate[] = [
  {
    id: 'recap',
    name: 'Weekly recap',
    description: "Pull this week's wins, draft a recap, queue Sunday send.",
    outputKinds: ['document'],
    steps: [
      {
        action: "Pull this week's top posts",
        uses: 'connected platforms',
        creates: 'top-3 list',
        requires_approval: false,
      },
      {
        action: 'Draft recap email',
        uses: 'top-3 list',
        creates: 'email draft',
        requires_approval: true,
      },
      {
        action: 'Queue Sunday send',
        uses: 'email draft',
        creates: 'scheduled send',
        requires_approval: false,
      },
    ],
  },
  {
    id: 'repurpose',
    name: 'Repurpose post',
    description: 'Take one post and reshape it for two more platforms.',
    outputKinds: ['post'],
    steps: [
      {
        action: 'Pick the source post',
        uses: 'recent posts',
        creates: 'reference',
        requires_approval: true,
      },
      {
        action: 'Reshape for two platforms',
        uses: 'reference',
        creates: '2 drafts',
        requires_approval: true,
      },
      {
        action: 'Schedule both',
        uses: 'drafts',
        creates: 'scheduled posts',
        requires_approval: false,
      },
    ],
  },
  {
    id: 'series',
    name: 'Three-part series',
    description: 'Outline, draft, and queue a three-post series on one topic.',
    outputKinds: ['post'],
    steps: [
      {
        action: 'Outline three angles',
        uses: 'topic',
        creates: 'outline',
        requires_approval: true,
      },
      {
        action: 'Draft all three posts',
        uses: 'outline',
        creates: '3 drafts',
        requires_approval: true,
      },
      {
        action: 'Schedule across the week',
        uses: 'drafts',
        creates: 'scheduled posts',
        requires_approval: false,
      },
    ],
  },
];

let nextId = 0;
function newStepId(): string {
  nextId += 1;
  return `step-${Date.now()}-${nextId}`;
}

function blankStep(): DraftStep {
  return { id: newStepId(), action: '', uses: '', creates: '', requires_approval: false };
}

function templateToDraft(template: WorkflowTemplate): {
  name: string;
  description: string;
  steps: DraftStep[];
  outputKinds: WorkflowOutputKind[];
} {
  return {
    name: template.name,
    description: template.description ?? '',
    steps: (template.steps ?? []).map((s) => ({
      id: newStepId(),
      action: s.action,
      uses: s.uses ?? '',
      creates: s.creates ?? '',
      requires_approval: s.requires_approval ?? false,
    })),
    outputKinds: (template.output_kinds ?? []) as WorkflowOutputKind[],
  };
}

/**
 * Mora's full-screen workflow editor (Mercury / Talvo pattern).
 *
 * Layout (FullScreenSplitDialog):
 *   ┌─ Top bar ──────────────────────────────────────────────────────────────┐
 *   │ ✕ │ New workflow                            [Cancel] [Save and use →] │
 *   ├─ Body ──────────────────────────────┬─────────────────────────────────┤
 *   │ Form (max-w-[600px], space-y-12)    │ Live preview (composed card)    │
 *   │   • Start from a template           │ <WorkflowPreviewCard>           │
 *   │   • Workflow (Name + One-liner)     │   updates as you type           │
 *   │   • Steps (sortable, dnd-kit)       │                                 │
 *   │   • Output (Discover-style pills)   │                                 │
 *   └─────────────────────────────────────┴─────────────────────────────────┘
 *
 * Dual mode:
 *   - Default: blank slate with starters at the top
 *   - editTemplate prop: prefills the form, top bar reads "Edit workflow"
 *     and the primary CTA reads "Save changes"
 */
export const NewWorkflowDialog: React.FC<NewWorkflowDialogProps> = ({
  open,
  onOpenChange,
  editTemplate = null,
}) => {
  const queryClient = useQueryClient();
  const isEdit = Boolean(editTemplate);
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [steps, setSteps] = React.useState<DraftStep[]>([blankStep()]);
  const [outputKinds, setOutputKinds] = React.useState<WorkflowOutputKind[]>([]);

  const reset = React.useCallback((): void => {
    setName('');
    setDescription('');
    setSteps([blankStep()]);
    setOutputKinds([]);
  }, []);

  // Populate form on open. If editTemplate is set, prefill from it; else reset.
  React.useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    if (editTemplate) {
      const draft = templateToDraft(editTemplate);
      setName(draft.name);
      setDescription(draft.description);
      setSteps(draft.steps.length > 0 ? draft.steps : [blankStep()]);
      setOutputKinds(draft.outputKinds);
    }
  }, [open, editTemplate, reset]);

  const applyStarter = (s: StarterTemplate): void => {
    setName(s.name);
    setDescription(s.description);
    setSteps(s.steps.map((st) => ({ ...st, id: newStepId() })));
    setOutputKinds([...s.outputKinds]);
  };

  const updateStep = (id: string, patch: Partial<DraftStep>): void => {
    setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };
  const addStep = (): void => {
    if (steps.length >= 6) return;
    setSteps((prev) => [...prev, blankStep()]);
  };
  const removeStep = (id: string): void => {
    setSteps((prev) => (prev.length === 1 ? prev : prev.filter((s) => s.id !== id)));
  };
  const toggleOutput = (kind: WorkflowOutputKind): void => {
    setOutputKinds((prev) =>
      prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind],
    );
  };

  // ── Sortable wiring ──
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSteps((prev) => {
      const oldIndex = prev.findIndex((s) => s.id === active.id);
      const newIndex = prev.findIndex((s) => s.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const isValid =
    name.trim().length > 0 && steps.length > 0 && steps.every((s) => s.action.trim().length > 0);

  // Live preview template — derived from current form state.
  const livePreview: WorkflowTemplate = React.useMemo(() => {
    const wfSteps: WorkflowStep[] = steps
      .filter((s) => s.action.trim().length > 0)
      .map((s, i) => ({
        step_id: i + 1,
        action: s.action.trim(),
        uses: s.uses.trim() || undefined,
        creates: s.creates.trim() || undefined,
        requires_approval: s.requires_approval || undefined,
      }));
    return {
      id: editTemplate?.id ?? 'draft',
      name: name.trim() || 'Untitled workflow',
      description: description.trim() || null,
      created_at: editTemplate?.created_at ?? new Date().toISOString(),
      output_kinds: outputKinds.length > 0 ? outputKinds : undefined,
      steps: wfSteps,
      version: editTemplate?.version ?? 1,
    };
  }, [editTemplate, name, description, steps, outputKinds]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const wfSteps: WorkflowStep[] = steps.map((s, i) => ({
        step_id: i + 1,
        action: s.action.trim(),
        uses: s.uses.trim() || undefined,
        creates: s.creates.trim() || undefined,
        requires_approval: s.requires_approval || undefined,
      }));

      if (isEdit && editTemplate) {
        const res = await campaignService.updateWorkflowTemplate(editTemplate.id, {
          name: name.trim(),
          description: description.trim(),
          steps: wfSteps,
          output_kinds: outputKinds.length > 0 ? outputKinds : null,
        });
        if (res.error) throw new Error(res.error.message);
        return res.data;
      }
      const res = await campaignService.createWorkflowTemplate({
        name: name.trim(),
        description: description.trim(),
        steps: wfSteps,
        output_kinds: outputKinds.length > 0 ? outputKinds : undefined,
      });
      if (res.error) throw new Error(res.error.message);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.templates() });
      toast.success(isEdit ? 'Workflow updated' : 'Custom workflow saved');
      onOpenChange(false);
    },
    onError: (err: Error) =>
      toast.error(
        err.message ?? (isEdit ? 'Could not update workflow' : 'Could not save workflow'),
      ),
  });

  // ── Render ──
  const headerActions = (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onOpenChange(false)}
        disabled={saveMutation.isPending}
      >
        Cancel
      </Button>
      <Button
        variant="neutral"
        size="sm"
        onClick={() => saveMutation.mutate()}
        disabled={!isValid || saveMutation.isPending}
        className="min-w-[140px]"
      >
        {saveMutation.isPending ? (
          <>
            <Spinner className="size-3.5" />
            Saving…
          </>
        ) : isEdit ? (
          'Save changes'
        ) : (
          <>
            Save and use
            <ArrowRightIcon className="size-3.5" weight="regular" aria-hidden />
          </>
        )}
      </Button>
    </>
  );

  const left = (
    <div className="mx-auto max-w-[600px] space-y-12 px-6 py-10 sm:px-10 sm:py-12">
      {/* Section 1: Start from a template (hidden in edit mode) */}
      {!isEdit && (
        <section>
          <SectionHead
            title="Start from a template"
            description="A starter pre-fills every field below. Or define your own from blank."
          />
          <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {STARTER_TEMPLATES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => applyStarter(s)}
                className="group flex h-full flex-col rounded-lg border border-border bg-background px-3 py-3 text-left transition-colors hover:border-foreground/30 hover:bg-muted/20"
              >
                <span className="text-caption font-medium text-foreground">{s.name}</span>
                <span className={cn('mt-1 line-clamp-2', ITEM_META)}>{s.description}</span>
                <span className={cn('mt-2', ITEM_META)}>{s.steps.length} steps</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Section 2: Identity (Name + One-liner). No section heading —
          inputs + their own labels carry it. */}
      <section className={cn(!isEdit && 'border-t border-border pt-10')}>
        <div className="grid gap-6">
          <div>
            <Label htmlFor="wf-name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="wf-name"
              size="sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Weekly newsletter recap"
              maxLength={80}
              autoFocus={!isEdit}
            />
          </div>
          <div>
            <Label htmlFor="wf-desc">One-liner</Label>
            <Textarea
              id="wf-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this ship?"
              rows={2}
              maxLength={280}
            />
          </div>
        </div>
      </section>

      {/* Section 3: Steps (sortable) */}
      <section className="border-t border-border pt-10">
        <div className="flex items-baseline justify-between">
          <SectionHead
            title="Steps"
            description="The agent runs these in order. Drag the handle to reorder."
          />
          <Button
            type="button"
            variant="flat"
            size="sm"
            onClick={addStep}
            disabled={steps.length >= 6}
            className="h-8 gap-1"
          >
            <PlusIcon className="size-3.5" weight="regular" />
            Add step
          </Button>
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <ol className="mt-6 space-y-3">
              {steps.map((step, idx) => (
                <WorkflowEditorStepRow
                  key={step.id}
                  step={step}
                  idx={idx}
                  canRemove={steps.length > 1}
                  onUpdate={(patch) => updateStep(step.id, patch)}
                  onRemove={() => removeStep(step.id)}
                />
              ))}
            </ol>
          </SortableContext>
        </DndContext>
      </section>

      {/* Section 4: Output (Discover-style brand-followed pills) */}
      <section className="border-t border-border pt-10">
        <SectionHead title="Output" description="What kinds of artifacts this workflow creates." />
        <div className="mt-6 flex flex-wrap gap-1.5">
          {ALL_OUTPUT_KINDS.map((kind) => (
            <DiscoverPill
              key={kind}
              label={OUTPUT_KIND_LABEL[kind]}
              selected={outputKinds.includes(kind)}
              onToggle={() => toggleOutput(kind)}
            />
          ))}
        </div>
      </section>
    </div>
  );

  const right = (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto p-6 lg:p-12">
        <div className="mx-auto w-full max-w-sm">
          <WorkflowLibraryCardPreview template={livePreview} ownerBadge={!isEdit} />
        </div>
      </div>
    </div>
  );

  return (
    <FullScreenSplitDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? 'Edit workflow' : 'New workflow'}
      description={
        isEdit
          ? 'Update this workflow. Changes apply to future runs only.'
          : 'Author a custom workflow. The preview on the right updates as you type.'
      }
      headerActions={headerActions}
      left={left}
      right={right}
    />
  );
};

NewWorkflowDialog.displayName = 'NewWorkflowDialog';

function SectionHead({ title, description }: { title: string; description: string }): JSX.Element {
  return (
    <div>
      <h2 className="text-sm font-normal text-foreground/85">{title}</h2>
      <p className="text-muted-foreground mt-1 text-sm">{description}</p>
    </div>
  );
}
